import {
  ListAccountsApiArg,
  V1PagingMeta,
  V1Relationships,
  useListAccountsQuery,
} from "@generated";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import type { BaseRelationship } from "@baseTypes";
import type { Account } from "../types";

export interface HookArgs {
  page?: ListAccountsApiArg["page"];
  filter?: ListAccountsApiArg["filter"];
}

const useListAccounts = ({ page, filter }: HookArgs) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { data, error, isLoading, refetch } = useListAccountsQuery({
    page,
    filter,
  });

  let accounts: Account[] = [];
  let customError: string | null = null;
  let pageMeta = {
    recordCount: 0,
    pageCount: 0,
  };

  if (data?.data) {
    try {
      accounts = data.data.map((account) => {
        return {
          name: account.attributes?.name || "",
          slug: account.attributes?.slug || "",
          id: account.attributes?.["account-id"] || "",
          customerId: account.attributes?.["customer-id"] || "",
          subscriptionId: account.attributes?.["subscription-id"] || "",
          rateType: account.attributes?.["rate-type"] || "",
          loeRounding: account.attributes?.["loe-rounding"] || "",
          shortDateFormat: account.attributes?.["short-date-format"] || "",
          longDateFormat: account.attributes?.["long-date-format"] || "",
          timeFormat: account.attributes?.["time-format"] || "",
          streetAddress: account.attributes?.["street-address"] || "",
          city: account.attributes?.city || "",
          state: account.attributes?.state || "",
          postalCode: account.attributes?.["postal-code"] || "",
          country: account.attributes?.country || "",
          timeZone: account.attributes?.["time-zone"] || "",
          planName: account.attributes?.["plan-name"] || "",
          settings: account.attributes?.settings || {},
          planDetails: account.attributes?.["plan-details"] || {},
        };
      });

      if (!data.meta) {
        throw new Error("Missing data.meta");
      }

      if (!data.meta["record-count"]) {
        throw new Error('Missing data.meta["record-count"]');
      }

      if (!data.meta["page-count"]) {
        throw new Error('Missing data.meta["page-count"]');
      }
      pageMeta = {
        recordCount: data.meta["record-count"],
        pageCount: data.meta["page-count"],
      };
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  return { accounts, error: error || customError, isLoading, pageMeta, refetch };
};

export default useListAccounts;
